/**
 * INVITEPAGE.JS v5 - MIT ANALYTICS TRACKING
 * 
 * ✅ v5: Analytics Tracking
 *        - usePublicPageView für Invite Page
 *        - Trackt Besucher ohne Login
 * ✅ v4: Weißes Design + Planivio Teal
 * ✅ v3: Planivio Rebranding
 * ✅ v2: QR-Code zur Landing Page
 * 
 * @version 5.0.0 - Analytics Tracking
 */

import React, { useState } from 'react';
import { useTranslation, LANGUAGES } from '../context/I18nContext';

// ✅ NEU v5: Analytics Import
import { usePublicPageView } from '../hooks/useAnalytics';

// ============================================================================
// PLANIVIO FARBEN
// ============================================================================
const PLANIVIO = {
  teal: '#4A90A4',
  green: '#6BA893',
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
  shadow: 'rgba(74, 144, 164, 0.3)'
};

const COLORS = {
  bgWhite: '#FFFFFF',
  bgLight: '#F8FAFC',
  bgSection: '#F1F5F9',
  textDark: '#1E293B',
  textMedium: '#475569',
  textLight: '#64748B',
  textMuted: '#94A3B8',
  border: '#E2E8F0',
  accent: PLANIVIO.teal,
  accentGreen: PLANIVIO.green
};

function InvitePage({ onNavigate }) {
  const { t, language, setLanguage, currentLanguage } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [qrLoaded, setQrLoaded] = useState(false);
  const [qrError, setQrError] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  
  // ✅ NEU v5: Pageview tracken (einmal beim Laden)
  usePublicPageView('invite');
  
  const landingUrl = 'https://app.planivio.de';
  const actualUrl = window.location.hostname === 'localhost' 
    ? landingUrl 
    : (window.location.origin || landingUrl);
  
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(actualUrl)}&format=png&margin=10`;
  const qrCodeFallback = `https://chart.googleapis.com/chart?cht=qr&chs=280x280&chl=${encodeURIComponent(actualUrl)}&choe=UTF-8`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(actualUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = actualUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('invite.share.title'),
          text: t('invite.share.text'),
          url: actualUrl
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      handleCopyLink();
    }
  };

  const handleQrError = () => {
    if (!qrError) {
      setQrError(true);
    }
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          .print-only {
            display: block !important;
          }
          
          body {
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .invite-container {
            background: white !important;
            color: black !important;
            padding: 40px !important;
          }
          
          .invite-card {
            border: 2px solid ${COLORS.accent} !important;
            box-shadow: none !important;
            page-break-inside: avoid;
          }
          
          .qr-code-wrapper {
            background: white !important;
          }
          
          .invite-title {
            color: ${COLORS.accent} !important;
            -webkit-text-fill-color: ${COLORS.accent} !important;
          }
          
          .invite-text {
            color: #333 !important;
          }
          
          .invite-url {
            color: ${COLORS.accent} !important;
          }
        }
        
        @media screen {
          .print-only {
            display: none;
          }
        }
        
        .invite-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .invite-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 50px ${PLANIVIO.shadow};
        }
        
        @media (max-width: 600px) {
          .invite-actions {
            flex-direction: column !important;
          }
          
          .invite-actions button {
            width: 100% !important;
          }
          
          .share-buttons {
            flex-direction: column !important;
          }
        }
      `}</style>

      <div className="invite-container" style={{ 
        background: COLORS.bgWhite, 
        minHeight: '100vh', 
        color: COLORS.textDark,
        padding: '20px'
      }}>
        
        {/* Navigation */}
        <nav className="no-print" style={{
          padding: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '800px',
          margin: '0 auto 20px',
          background: COLORS.bgWhite,
          borderBottom: `1px solid ${COLORS.border}`
        }}>
          <div 
            onClick={() => onNavigate && onNavigate('landing')}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
          >
            {/* ✅ v4: Echtes Planivio Logo */}
            <img 
              src="/images/Logos/planivio_logo_primary_400px.png" 
              alt="Planivio" 
              style={{ height: '36px', width: 'auto' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div style={{ display: 'none', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '2rem' }}>📋</span>
              <span style={{ fontSize: '1.5rem', fontWeight: 800, color: COLORS.accent }}>planivio</span>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Language Switcher */}
            <button
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              style={{
                padding: '8px 14px',
                background: COLORS.bgLight,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: COLORS.textMedium,
                cursor: 'pointer',
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              <span>{currentLanguage.flag}</span>
              <span>{currentLanguage.code.toUpperCase()}</span>
              <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>▼</span>
            </button>
            
            <button
              onClick={() => onNavigate && onNavigate('landing')}
              style={{
                padding: '10px 24px',
                background: COLORS.bgLight,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '10px',
                color: COLORS.textMedium,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              ← {t('common.back')}
            </button>
          </div>
        </nav>

        {/* Main Card */}
        <div style={{ maxWidth: '500px', margin: '0 auto' }}>
          
          <div className="invite-card" style={{
            background: COLORS.bgWhite,
            border: `2px solid ${COLORS.border}`,
            borderRadius: '24px',
            padding: '40px 30px',
            textAlign: 'center',
            boxShadow: `0 10px 40px ${PLANIVIO.shadow}`
          }}>
            
            {/* Logo */}
            <div style={{ marginBottom: '20px' }}>
              <img 
                src="/images/Logos/planivio_logo_primary_400px.png" 
                alt="Planivio" 
                style={{ height: '60px', width: 'auto' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <span style={{ fontSize: '4rem', display: 'none' }}>📋</span>
            </div>

            {/* Title */}
            <h1 className="invite-title" style={{
              fontSize: '2rem',
              fontWeight: 800,
              marginBottom: '10px',
              background: PLANIVIO.gradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {t('invite.title')}
            </h1>

            {/* Subtitle */}
            <p className="invite-text" style={{
              color: COLORS.textLight,
              fontSize: '1.05rem',
              marginBottom: '30px',
              lineHeight: 1.6
            }}>
              {t('invite.subtitle.try')} <strong style={{ color: COLORS.textDark }}>Planivio</strong> {t('invite.subtitle.smartApp')}
              <br />{t('invite.subtitle.freeBeta')}
            </p>

            {/* QR Code */}
            <div className="qr-code-wrapper" style={{
              background: COLORS.bgLight,
              borderRadius: '20px',
              padding: '20px',
              display: 'inline-block',
              marginBottom: '25px',
              border: `1px solid ${COLORS.border}`
            }}>
              {!qrLoaded && !qrError && (
                <div style={{
                  width: '260px',
                  height: '260px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '10px',
                  color: COLORS.accent
                }}>
                  <span style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }}>⏳</span>
                  <span style={{ fontSize: '0.9rem', color: COLORS.textLight }}>QR-Code lädt...</span>
                </div>
              )}
              <img 
                src={qrError ? qrCodeFallback : qrCodeUrl}
                alt="QR Code zu Planivio"
                style={{
                  width: '260px',
                  height: '260px',
                  display: qrLoaded ? 'block' : 'none',
                  borderRadius: '8px'
                }}
                onLoad={() => setQrLoaded(true)}
                onError={handleQrError}
              />
            </div>

            {/* URL */}
            <div style={{ marginBottom: '25px' }}>
              <p style={{ color: COLORS.textMuted, fontSize: '0.85rem', marginBottom: '8px' }}>
                {t('invite.orVisit')}
              </p>
              <div className="invite-url" style={{
                background: COLORS.bgLight,
                padding: '12px 20px',
                borderRadius: '10px',
                fontFamily: 'monospace',
                fontSize: '1rem',
                color: COLORS.accent,
                fontWeight: 600,
                wordBreak: 'break-all',
                border: `1px solid ${COLORS.border}`
              }}>
                {actualUrl}
              </div>
            </div>

            {/* Features Mini */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
              flexWrap: 'wrap',
              marginBottom: '25px',
              padding: '15px',
              background: COLORS.bgSection,
              borderRadius: '12px'
            }}>
              <span style={{ fontSize: '0.85rem', color: COLORS.textMedium }}>🛒 {t('invite.features.shopping')}</span>
              <span style={{ fontSize: '0.85rem', color: COLORS.textMedium }}>🍳 {t('invite.features.recipes')}</span>
              <span style={{ fontSize: '0.85rem', color: COLORS.textMedium }}>📅 {t('invite.features.calendar')}</span>
              <span style={{ fontSize: '0.85rem', color: COLORS.textMedium }}>🤖 {t('invite.features.ai')}</span>
            </div>

            {/* Beta Badge */}
            <div style={{
              display: 'inline-block',
              padding: '8px 20px',
              background: PLANIVIO.gradient,
              borderRadius: '50px',
              fontSize: '0.85rem',
              fontWeight: 700,
              color: 'white'
            }}>
              🎉 {t('invite.freeDuringBeta')}
            </div>

          </div>

          {/* Action Buttons */}
          <div className="invite-actions no-print" style={{
            display: 'flex',
            gap: '12px',
            marginTop: '24px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={handleCopyLink}
              style={{
                padding: '14px 28px',
                background: copied 
                  ? COLORS.accentGreen
                  : PLANIVIO.gradient,
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s',
                boxShadow: `0 4px 15px ${PLANIVIO.shadow}`
              }}
            >
              {copied ? `✓ ${t('invite.buttons.copied')}` : `📋 ${t('invite.buttons.copyLink')}`}
            </button>
            
            <button
              onClick={handleShare}
              style={{
                padding: '14px 28px',
                background: COLORS.bgWhite,
                border: `2px solid ${COLORS.border}`,
                borderRadius: '12px',
                color: COLORS.textMedium,
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              📤 {t('invite.buttons.share')}
            </button>
            
            <button
              onClick={handlePrint}
              style={{
                padding: '14px 28px',
                background: COLORS.bgWhite,
                border: `2px solid ${COLORS.border}`,
                borderRadius: '12px',
                color: COLORS.textMedium,
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              🖨️ {t('invite.buttons.print')}
            </button>
          </div>

          {/* Social Share Buttons */}
          <div className="share-buttons no-print" style={{
            display: 'flex',
            gap: '10px',
            marginTop: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(t('invite.share.whatsappText') + ' ' + actualUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '10px 20px',
                background: '#25D366',
                borderRadius: '10px',
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              💬 WhatsApp
            </a>
            
            <a
              href={`mailto:?subject=${encodeURIComponent(t('invite.share.emailSubject'))}&body=${encodeURIComponent(t('invite.share.emailBody') + ' ' + actualUrl)}`}
              style={{
                padding: '10px 20px',
                background: PLANIVIO.gradient,
                borderRadius: '10px',
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              📧 {t('invite.buttons.email')}
            </a>
            
            <a
              href={`https://telegram.me/share/url?url=${encodeURIComponent(actualUrl)}&text=${encodeURIComponent(t('invite.share.telegramText'))}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '10px 20px',
                background: '#0088cc',
                borderRadius: '10px',
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ✈️ Telegram
            </a>
          </div>

          {/* Hinweis */}
          <p className="no-print" style={{
            textAlign: 'center',
            color: COLORS.textMuted,
            fontSize: '0.8rem',
            marginTop: '30px'
          }}>
            💡 {t('invite.printTip')}
          </p>

        </div>

        {/* Print Footer */}
        <div className="print-only" style={{
          textAlign: 'center',
          marginTop: '40px',
          paddingTop: '20px',
          borderTop: '1px solid #ddd'
        }}>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>
            🇩🇪 Planivio - {t('landing.footer.madeIn')}
          </p>
        </div>

      </div>

      {/* Language Dropdown */}
      {showLanguageMenu && (
        <>
          <div 
            onClick={() => setShowLanguageMenu(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 99998
            }}
          />
          <div style={{
            position: 'fixed',
            top: '70px',
            right: '20px',
            background: COLORS.bgWhite,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            padding: '8px',
            minWidth: '160px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            zIndex: 99999
          }}>
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setShowLanguageMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: language === lang.code 
                    ? PLANIVIO.gradient 
                    : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: language === lang.code ? 'white' : COLORS.textDark,
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s',
                  textAlign: 'left',
                  marginBottom: '4px'
                }}
                onMouseEnter={(e) => {
                  if (language !== lang.code) {
                    e.target.style.background = COLORS.bgLight;
                  }
                }}
                onMouseLeave={(e) => {
                  if (language !== lang.code) {
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: '1.3rem' }}>{lang.flag}</span>
                <span>{lang.name}</span>
                {language === lang.code && (
                  <span style={{ marginLeft: 'auto', opacity: 0.8 }}>✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

export default InvitePage;